// ============================================================================
// React Query Global Configuration
// Phase 3: Replaces homegrown useFetch with proper cache + dedup + retry
// ============================================================================

import { QueryClient } from '@tanstack/react-query'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Show stale data while refetching - never blank screen on transient failures
      staleTime: 30_000, // 30s - data is "fresh" for 30s, no refetch
      gcTime: 5 * 60_000, // 5min - unused cache entries kept for 5min (was cacheTime in v4)
      retry: 2, // Retry failed queries twice
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10000),
      refetchOnWindowFocus: true, // Refetch when tab regains focus (catches stale data)
      refetchOnReconnect: true, // Refetch when network reconnects
      // NOTE: v4's `keepPreviousData: true` was removed in React Query v5.
      // Per-query "show last good data while refetching" is now done with
      // `placeholderData: keepPreviousData` from '@tanstack/react-query'.
    },
    mutations: {
      retry: 0, // Don't retry mutations - user should decide
    },
  },
})

// ============================================================================
// QUERY KEY FACTORY
// Hierarchical keys enable precise invalidation:
//   queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
//   -> invalidates client list AND all client details
//
//   queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail('clv_abc') })
//   -> invalidates only that client's detail, not the list
// ============================================================================

export const queryKeys = {
  // Dashboard
  dashboard: {
    all: ['dashboard'],
    stats: () => ['dashboard', 'stats'],
    attention: () => ['dashboard', 'attention'],
  },

  // Clients
  clients: {
    all: ['clients'],
    list: (filters) => ['clients', 'list', filters],
    detail: (id) => ['clients', 'detail', id],
    leads: (id, filters) => ['clients', id, 'leads', filters],
    campaigns: (id) => ['clients', id, 'campaigns'],
    stats: (id) => ['clients', id, 'stats'],
    billing: (id) => ['clients', id, 'billing'],
    activity: (id, filters) => ['clients', id, 'activity', filters],
    notes: (id) => ['clients', id, 'notes'],
    contracts: (id) => ['clients', id, 'contracts'],
  },

  // Campaigns
  campaigns: {
    all: ['campaigns'],
    list: (filters) => ['campaigns', 'list', filters],
    detail: (id) => ['campaigns', 'detail', id],
    sequences: (id) => ['campaigns', id, 'sequences'],
    schedule: (id) => ['campaigns', id, 'schedule'],
    accounts: (id) => ['campaigns', id, 'accounts'],
    variants: (id, step) => ['campaigns', id, 'variants', step],
    leads: (id, filters) => ['campaigns', id, 'leads', filters],
    analytics: {
      steps: (id) => ['campaigns', id, 'analytics', 'steps'],
      daily: (id, days) => ['campaigns', id, 'analytics', 'daily', days],
      time: (id, days) => ['campaigns', id, 'analytics', 'time', days],
      subjects: (id) => ['campaigns', id, 'analytics', 'subjects'],
      abTests: (id) => ['campaigns', id, 'analytics', 'ab-tests'],
      funnel: (id) => ['campaigns', id, 'analytics', 'funnel'],
      bounces: (id) => ['campaigns', id, 'analytics', 'bounces'],
      engagement: (id) => ['campaigns', id, 'analytics', 'engagement'],
      geography: (id) => ['campaigns', id, 'analytics', 'geography'],
      sentiment: (id) => ['campaigns', id, 'analytics', 'sentiment'],
    },
  },

  // Leads
  leads: {
    all: ['leads'],
    list: (filters) => ['leads', 'list', filters],
    detail: (id) => ['leads', 'detail', id],
    notes: (id) => ['leads', id, 'notes'],
    tasks: (id) => ['leads', id, 'tasks'],
    tags: (id) => ['leads', id, 'tags'],
    timeline: (id) => ['leads', id, 'timeline'],
    emails: (id) => ['leads', id, 'emails'],
    variables: (id, campaignId) => ['leads', id, 'variables', campaignId],
  },

  // Replies
  replies: {
    all: ['replies'],
    list: (filters) => ['replies', 'list', filters],
    detail: (id) => ['replies', 'detail', id],
    stats: () => ['replies', 'stats'],
  },

  // Contracts
  contracts: {
    all: ['contracts'],
    list: (filters) => ['contracts', 'list', filters],
    detail: (id) => ['contracts', 'detail', id],
    signers: () => ['contracts', 'signers'],
  },

  // Prospects (Sales Pipeline)
  prospects: {
    all: ['prospects'],
    list: (filters) => ['prospects', 'list', filters],
    detail: (id) => ['prospects', 'detail', id],
    stats: () => ['prospects', 'stats'],
    activities: (id) => ['prospects', id, 'activities'],
  },

  // Alerts
  alerts: {
    all: ['alerts'],
    list: (filters) => ['alerts', 'list', filters],
    stats: () => ['alerts', 'stats'],
  },

  // System
  system: {
    errors: (filters) => ['system', 'errors', filters],
    errorCategories: () => ['system', 'error-categories'],
    queues: () => ['system', 'queues'],
    killSwitch: () => ['system', 'kill-switch'],
    quarantine: (filters) => ['system', 'quarantine', filters],
    observability: {
      overview: (range) => ['system', 'observability', 'overview', range],
      errors: (range) => ['system', 'observability', 'errors', range],
      apiHealth: (range) => ['system', 'observability', 'api-health', range],
      workers: (range) => ['system', 'observability', 'workers', range],
      timeseries: (range) => ['system', 'observability', 'timeseries', range],
    },
  },

  // Analytics
  analytics: {
    overview: (days) => ['analytics', 'overview', days],
    campaigns: (filters) => ['analytics', 'campaigns', filters],
    accounts: (filters) => ['analytics', 'accounts', filters],
    trends: (filters) => ['analytics', 'trends', filters],
    leaderboard: (filters) => ['analytics', 'leaderboard', filters],
    costs: (days) => ['analytics', 'costs', days],
  },

  // Email Accounts
  emailAccounts: {
    all: ['email-accounts'],
    list: (filters) => ['email-accounts', 'list', filters],
    detail: (email) => ['email-accounts', 'detail', email],
    analytics: (email, days) => ['email-accounts', email, 'analytics', days],
  },

  // Deliverability
  deliverability: {
    all: ['deliverability'],
    overview: () => ['deliverability', 'overview'],
    domains: () => ['deliverability', 'domains'],
    blacklist: (filters) => ['deliverability', 'blacklist', filters],
    bounces: (days) => ['deliverability', 'bounces', days],
    spam: (days) => ['deliverability', 'spam', days],
  },

  // Niche Briefs / Lead Discovery
  nicheBriefs: {
    all: ['niche-briefs'],
    list: (filters) => ['niche-briefs', 'list', filters],
    detail: (id) => ['niche-briefs', 'detail', id],
    related: (clientId, excludeId) => ['niche-briefs', 'related', clientId, excludeId],
    benchmarks: () => ['niche-briefs', 'benchmarks'],
    activity: (id) => ['niche-briefs', id, 'activity'],
    templates: (id) => ['niche-briefs', id, 'templates'],
    discoveredLeads: (id, filters) => ['niche-briefs', id, 'discovered-leads', filters],
  },

  // Industries (Phase 8 - merged Playbooks + Templates + Subject Lines)
  industries: {
    all: ['playbooks'],
    list: () => ['playbooks', 'list'],
    detail: (id) => ['playbooks', 'detail', id],
    subjectLines: (id) => ['playbooks', id, 'subject-lines'],
  },

  // Playbooks (backward compat alias - same cache keys as industries)
  playbooks: {
    all: ['playbooks'],
    list: () => ['playbooks', 'list'],
    detail: (id) => ['playbooks', 'detail', id],
  },

  // Sub-Industries (Three-Layer Architecture)
  subIndustries: {
    all: ['sub-industries'],
    list: (playbookId) => ['sub-industries', 'list', playbookId],
    detail: (id) => ['sub-industries', 'detail', id],
  },

  // Sub-Industry ICPs (Three-Layer Architecture - Side B)
  subIndustryIcps: {
    all: ['sub-industry-icps'],
    list: (subIndustryId) => ['sub-industry-icps', 'list', subIndustryId],
    detail: (id) => ['sub-industry-icps', 'detail', id],
  },

  // Lead Discovery Personas (Phase 8)
  personas: {
    all: ['personas'],
    list: () => ['personas', 'list'],
    detail: (id) => ['personas', 'detail', id],
  },

  // Templates
  templates: {
    all: ['templates'],
    list: () => ['templates', 'list'],
    detail: (industry) => ['templates', 'detail', industry],
    costs: (days) => ['templates', 'costs', days],
    sampleLeads: (industry) => ['templates', 'sample-leads', industry],
  },

  // Settings
  settings: {
    featureFlags: () => ['settings', 'feature-flags'],
    notifications: () => ['settings', 'notifications'],
  },

  // Support
  support: {
    tickets: (filters) => ['support', 'tickets', filters],
    detail: (id) => ['support', 'detail', id],
    cannedResponses: () => ['support', 'canned-responses'],
  },

  // Block List
  blockList: {
    list: (filters) => ['block-list', filters],
  },

  // Pipeline
  pipeline: {
    stats: (nicheId) => ['pipeline', 'stats', nicheId],
    nichePerformance: () => ['pipeline', 'niche-performance'],
  },

  // Portal
  portal: {
    all: ['portal'],
    overview: () => ['portal', 'overview'],
  },

  // Reports
  reports: {
    all: ['reports'],
    recent: () => ['reports', 'recent'],
    scheduled: () => ['reports', 'scheduled'],
  },

  // Activity
  activity: {
    list: (filters) => ['activity', 'list', filters],
  },

  // Auth
  auth: {
    me: () => ['auth', 'me'],
  },
}
